import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Star, ArrowRight, ShoppingBag, Info } from 'lucide-react';

const Landing = () => {
    const navigate = useNavigate();

    const stats = [
        { label: 'Rating', value: '4.8', icon: <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" /> },
        { label: 'Reviews', value: '500+', icon: <Info className="w-5 h-5 text-blue-500" /> },
        { label: 'Experience', value: '15+ Yrs', icon: <ShoppingBag className="w-5 h-5 text-primary" /> },
    ];

    return (
        <div className="min-h-screen bg-background flex flex-col font-sans">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-serif font-bold text-primary tracking-tight">RUDRAKSH 925</span>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img 
                        src="/hero-banner.png" 
                        alt="Fine Silver Jewelry" 
                        className="w-full h-full object-cover brightness-[0.4]"
                    />
                </div>
                
                <div className="relative z-10 text-center px-6 max-w-4xl">
                    <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 drop-shadow-xl">
                        Rudraksh 925 Silver
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-200 mb-8 font-light tracking-wide max-w-2xl mx-auto">
                        Exquisite craftsmanship meeting timeless elegance. Bengaluru's premier destination for authentic 925 silver jewelry.
                    </p>
                    <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                        <Button 
                            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-8 py-6 rounded-full font-bold shadow-lg transition-transform hover:scale-105"
                            onClick={() => window.open('https://www.google.com/maps/place/Rudraksh+925+Silver/@12.967482,77.5804394,17z/data=!4m14!1m7!3m6!1s0x3bae17942cb950f7:0xe97c69173967c839!2sRudraksh+925+Silver!8m2!3d12.967482!4d77.5804394!16s%2Fg%2F11vhggt21r!3m5!1s0x3bae17942cb950f7:0xe97c69173967c839!8m2!3d12.967482!4d77.5804394!16s%2Fg%2F11vhggt21r?entry=ttu&g_ep=EgoyMDI2MDMwOS4wIKXMDSoASAFQAw%3D%3D', '_blank')}
                        >
                            <MapPin className="mr-2 h-5 w-5" /> Get Directions
                        </Button>
                        <Button 
                            variant="outline" 
                            className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 text-lg px-8 py-6 rounded-full font-semibold"
                            onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                        >
                            Discover More
                        </Button>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-background to-transparent z-0"></div>
            </section>

            {/* Quick Stats */}
            <section className="bg-primary text-primary-foreground py-12 px-6">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {stats.map((stat, idx) => (
                        <div key={idx} className="flex flex-col items-center text-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                            <div className="mb-4 bg-white/10 p-4 rounded-full">
                                {stat.icon}
                            </div>
                            <div className="text-3xl font-bold mb-1">{stat.value}</div>
                            <div className="text-white/60 text-sm font-medium uppercase tracking-widest">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Info Section */}
            <section id="about" className="py-24 px-6 max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-8 leading-tight">
                            A Tradition of<br />Silver Excellence
                        </h2>
                        <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                            <p>
                                Welcome to Rudraksh 925 Silver, where every piece tells a story of heritage and quality. Located in the heart of Bengaluru, we specialize in premium 925 sterling silver jewelry that balances traditional designs with modern aesthetics.
                            </p>
                            <p>
                                Our curated collections feature everything from intricate ethnic pieces to sleek contemporary ornaments, ensuring that there's something perfect for every occasion and personality.
                            </p>
                        </div>
                        
                        <div className="mt-12 space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="bg-accent p-3 rounded-lg text-accent-foreground shrink-0">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg">Visit Our Store</h4>
                                    <p className="text-muted-foreground">4th Floor, M.S Achha Avenue, No 47, Murugesh Modaliar Ln, near Rajatha Hotel, Bengaluru, Karnataka 560002</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="bg-accent p-3 rounded-lg text-accent-foreground shrink-0">
                                    <Phone className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg">Call Us</h4>
                                    <p className="text-muted-foreground">078998 02582</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="relative">
                        <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500">
                            <img 
                                src="/hero-banner.png" 
                                alt="Store Details" 
                                className="w-full h-full object-cover grayscale-[0.3] hover:grayscale-0 transition-all duration-500"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-secondary text-secondary-foreground p-8 rounded-2xl shadow-xl max-w-xs">
                            <div className="flex items-center gap-1 mb-2">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 fill-current" />
                                ))}
                            </div>
                            <p className="font-bold text-lg mb-1 italic">"The best silver jewelry collection in Bengaluru. Exceptional quality!"</p>
                            <p className="text-sm opacity-80">- Satisfied Customer</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-muted py-24 px-6 text-center">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl md:text-5xl font-serif font-bold text-primary mb-6">
                        Experience Elegance in Person
                    </h2>
                    <p className="text-xl text-muted-foreground mb-10">
                        Drop by our showroom to explore our latest collection of handcrafted silver masterpieces.
                    </p>
                    <Button 
                        size="lg" 
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-8 text-xl rounded-2xl font-bold shadow-xl flex mx-auto items-center gap-3 transition-all hover:gap-5"
                        onClick={() => window.open('https://www.google.com/maps/place/Rudraksh+925+Silver/@12.967482,77.5804394,17z/data=!4m14!1m7!3m6!1s0x3bae17942cb950f7:0xe97c69173967c839!2sRudraksh+925+Silver!8m2!3d12.967482!4d77.5804394!16s%2Fg%2F11vhggt21r', '_blank')}
                    >
                        Visit Store Now <ArrowRight strokeWidth={3} />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="mt-auto py-12 px-6 border-t border-border bg-white text-center">
                <p 
                    className="text-muted-foreground font-medium cursor-default select-none"
                    onClick={(e) => {
                        if (e.detail === 3) {
                            localStorage.setItem('login_access_at', Date.now().toString());
                            navigate('/login');
                        }
                    }}
                >
                    © {new Date().getFullYear()} Rudraksh 925 Silver. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default Landing;
